import { useRef, useState } from 'react';
import {
  Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight,
  List, ListOrdered, Link, Type, Palette, Image, Video, Undo, Redo, Code
} from 'lucide-react';

const CORES = ['#13305f', '#16a34a', '#dc2626', '#16a34a', '#d97706', '#0891b2', '#13305f', '#ec4899', '#555555', '#888888'];
const FONTES = ['14px', '16px', '18px', '22px', '28px', '36px'];

export default function EmailEditor({ value, onChange, onUploadImage }) {
  const editorRef = useRef(null);
  const [mostrarCores, setMostrarCores] = useState(false);
  const [mostrarFontes, setMostrarFontes] = useState(false);
  const [modoHtml, setModoHtml] = useState(false);
  const [htmlRaw, setHtmlRaw] = useState('');
  const fileInputRef = useRef(null);

  function exec(cmd, val = null) {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
    syncHtml();
  }

  function syncHtml() {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }

  function handlePaste(e) {
    e.preventDefault();
    const text = e.clipboardData.getData('text/html') || e.clipboardData.getData('text/plain');
    document.execCommand('insertHTML', false, text);
    syncHtml();
  }

  function handleLink() {
    const url = prompt('URL do link:', 'https://');
    if (url) exec('createLink', url);
  }

  function handleImageUrl() {
    const url = prompt('URL da imagem:', 'https://');
    if (url) {
      exec('insertHTML', `<img src="${url}" style="max-width:100%;border-radius:8px;margin:8px 0;" />`);
    }
  }

  function handleVideoUrl() {
    const url = prompt('URL do vídeo (YouTube, Vimeo):', 'https://');
    if (url) {
      exec('insertHTML', `
        <div style="text-align:center;margin:12px 0;">
          <a href="${url}" style="color:#16a34a;font-weight:500;">▶️ Assistir vídeo</a>
        </div>
      `);
    }
  }

  async function handleImageUpload(e) {
    const arquivo = e.target.files[0];
    if (!arquivo) return;
    if (onUploadImage) {
      try {
        const url = await onUploadImage(arquivo);
        if (url) {
          exec('insertHTML', `<img src="${url}" style="max-width:100%;border-radius:8px;margin:8px 0;" />`);
        }
      } catch {
        alert('Erro ao enviar imagem');
      }
    }
    e.target.value = '';
  }

  function toggleModoHtml() {
    if (modoHtml) {
      // Voltando para visual — aplica o HTML editado
      if (editorRef.current) {
        editorRef.current.innerHTML = htmlRaw;
        onChange(htmlRaw);
      }
    } else {
      // Indo para HTML — captura o estado atual
      setHtmlRaw(editorRef.current?.innerHTML || value || '');
    }
    setModoHtml(!modoHtml);
  }

  const btnStyle = (active = false) => ({
    padding: '5px 7px', borderRadius: '5px', border: 'none',
    background: active ? '#f0fdf4' : 'transparent', cursor: 'pointer',
    color: active ? '#12833b' : '#555', display: 'flex', alignItems: 'center',
    justifyContent: 'center', transition: 'background 0.15s'
  });

  const separador = <div style={{ width: '1px', height: '20px', background: '#e5e5e5', margin: '0 2px' }} />;

  return (
    <div style={{ border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden', background: '#fff' }}>
      {/* Barra de ferramentas */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '2px', padding: '6px 8px',
        borderBottom: '1px solid #eee', background: '#fafafa', alignItems: 'center'
      }}>
        <button type="button" onClick={() => exec('bold')} style={btnStyle()} title="Negrito">
          <Bold size={15} />
        </button>
        <button type="button" onClick={() => exec('italic')} style={btnStyle()} title="Itálico">
          <Italic size={15} />
        </button>
        <button type="button" onClick={() => exec('underline')} style={btnStyle()} title="Sublinhado">
          <Underline size={15} />
        </button>

        {separador}

        <div style={{ position: 'relative' }}>
          <button type="button" onClick={() => { setMostrarFontes(!mostrarFontes); setMostrarCores(false); }}
            style={btnStyle(mostrarFontes)} title="Tamanho do texto">
            <Type size={15} />
          </button>
          {mostrarFontes && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, zIndex: 10,
              background: '#fff', border: '1px solid #ddd', borderRadius: '6px',
              padding: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}>
              {FONTES.map(size => (
                <button key={size} type="button"
                  onClick={() => { exec('fontSize', '7'); setMostrarFontes(false);
                    // Hack: fontSize aceita 1-7, usamos CSS depois
                    setTimeout(() => {
                      const els = editorRef.current?.querySelectorAll('font[size="7"]');
                      els?.forEach(el => { el.removeAttribute('size'); el.style.fontSize = size; });
                      syncHtml();
                    }, 10);
                  }}
                  style={{ display: 'block', width: '100%', padding: '4px 12px', border: 'none',
                    background: 'transparent', cursor: 'pointer', fontSize: size, textAlign: 'left' }}>
                  Aa
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ position: 'relative' }}>
          <button type="button" onClick={() => { setMostrarCores(!mostrarCores); setMostrarFontes(false); }}
            style={btnStyle(mostrarCores)} title="Cor do texto">
            <Palette size={15} />
          </button>
          {mostrarCores && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, zIndex: 10,
              background: '#fff', border: '1px solid #ddd', borderRadius: '6px',
              padding: '6px', display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)',
              gap: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}>
              {CORES.map(cor => (
                <button key={cor} type="button"
                  onClick={() => { exec('foreColor', cor); setMostrarCores(false); }}
                  style={{
                    width: '24px', height: '24px', borderRadius: '4px',
                    background: cor, border: '1px solid #ddd', cursor: 'pointer'
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {separador}

        <button type="button" onClick={() => exec('justifyLeft')} style={btnStyle()} title="Alinhar esquerda">
          <AlignLeft size={15} />
        </button>
        <button type="button" onClick={() => exec('justifyCenter')} style={btnStyle()} title="Centralizar">
          <AlignCenter size={15} />
        </button>
        <button type="button" onClick={() => exec('justifyRight')} style={btnStyle()} title="Alinhar direita">
          <AlignRight size={15} />
        </button>

        {separador}

        <button type="button" onClick={() => exec('insertUnorderedList')} style={btnStyle()} title="Lista">
          <List size={15} />
        </button>
        <button type="button" onClick={() => exec('insertOrderedList')} style={btnStyle()} title="Lista numerada">
          <ListOrdered size={15} />
        </button>

        {separador}

        <button type="button" onClick={handleLink} style={btnStyle()} title="Inserir link">
          <Link size={15} />
        </button>
        <button type="button" onClick={handleImageUrl} style={btnStyle()} title="Imagem por URL">
          <Image size={15} />
        </button>
        <button type="button" onClick={() => fileInputRef.current?.click()} style={btnStyle()} title="Upload de imagem">
          <span style={{ fontSize: '12px' }}>📁</span>
        </button>
        <button type="button" onClick={handleVideoUrl} style={btnStyle()} title="Inserir vídeo">
          <Video size={15} />
        </button>

        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload}
          style={{ display: 'none' }} />

        {separador}

        <button type="button" onClick={() => exec('undo')} style={btnStyle()} title="Desfazer">
          <Undo size={15} />
        </button>
        <button type="button" onClick={() => exec('redo')} style={btnStyle()} title="Refazer">
          <Redo size={15} />
        </button>

        <div style={{ marginLeft: 'auto' }}>
          <button type="button" onClick={toggleModoHtml} style={btnStyle(modoHtml)} title="Editar HTML">
            <Code size={15} />
            <span style={{ fontSize: '11px', marginLeft: '4px' }}>{modoHtml ? 'Visual' : 'HTML'}</span>
          </button>
        </div>
      </div>

      {/* Área de edição */}
      {modoHtml ? (
        <textarea
          value={htmlRaw}
          onChange={e => setHtmlRaw(e.target.value)}
          style={{
            width: '100%', minHeight: '300px', padding: '16px', border: 'none',
            fontSize: '13px', fontFamily: 'monospace', resize: 'vertical',
            boxSizing: 'border-box', background: '#13305f', color: '#e2e8f0',
            outline: 'none'
          }}
        />
      ) : (
        <div
          ref={editorRef}
          contentEditable
          onInput={syncHtml}
          onPaste={handlePaste}
          dangerouslySetInnerHTML={{ __html: value || '' }}
          style={{
            minHeight: '300px', padding: '16px', outline: 'none',
            fontSize: '15px', lineHeight: 1.7, color: '#333',
            overflowY: 'auto', maxHeight: '500px'
          }}
        />
      )}
    </div>
  );
}